/** Shared numeric helpers for the trading-analytics chart components. */

export function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/** Evenly-spaced indices, capped at `max`, always including the first/last point. */
export function pickTickIndices(n, max) {
  if (n <= 0) return [];
  if (n <= max) return Array.from({ length: n }, (_, i) => i);
  const step = (n - 1) / (max - 1);
  return Array.from({ length: max }, (_, i) => Math.round(i * step));
}

/**
 * Simple least-squares linear regression over `{ x, y }` points.
 * Returns the `y = slope * x + intercept` fit; degenerate inputs (0 or 1
 * point, or every point sharing the same x) fall back to a flat line.
 *
 * @param {{ x: number, y: number }[]} points
 * @returns {{ slope: number, intercept: number }}
 */
export function computeLinearRegression(points) {
  const n = points.length;
  if (n === 0) return { slope: 0, intercept: 0 };
  if (n === 1) return { slope: 0, intercept: points[0].y };

  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  for (const { x, y } of points) {
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
  }

  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return { slope: 0, intercept: sumY / n };

  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}
